import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {TaskEditor, Translate, translate} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';

import backend from 'utils/backend';
import moment from 'moment';

const MyDay = (props) => {
  const {
    user,
    taskRefreshSetMyTasksCount,
    taskRefreshIncrementMyTasksCount,
    taskRefreshDecrementMyTasksCount,
    statusProgressWrap: progressWrap,
  } = props;

  const [todaysTasks, setTodaysTasks] = React.useState([]);
  const [dueTodayCount, setDueTodayCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editingTaskRn, setEditingTaskRn] = React.useState(null);

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setTodaysTasks([]);
      setDueTodayCount(0);
      taskRefreshSetMyTasksCount(0);
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const oneWeekAgoUtc = moment().startOf('day').subtract(1, 'week').utc();
        const endOfTodayUtc = moment().endOf('day').utc();
        const startDate = Math.floor(new Date(oneWeekAgoUtc.toISOString()).getTime());
        const endOfTodayDate = Math.floor(new Date(endOfTodayUtc.toISOString()).getTime());
        const taskSortBy = {sortKey: 'dueDate', sortDirection: 'ASC'};
        const userTaskFilters = [
          {key: 'resourceRn', value: user.userRn},
          {key: 'dueDate', value: `[${startDate} TO ${endOfTodayDate}]`},
          {key: 'status', value: ['OPEN', 'COMPLETE']},
        ];
        let allUserTasks = [];
        let lastEvaluatedKey = null;

        while (lastEvaluatedKey !== undefined) {
          const userTaskResponse = await backend.task.queryResourceTasks(userTaskFilters, taskSortBy, lastEvaluatedKey);

          allUserTasks = [...allUserTasks, ...(userTaskResponse?.list || [])];
          lastEvaluatedKey = userTaskResponse?.lastEvaluatedKey;

          if (!lastEvaluatedKey) {
            break;
          }
        }

        const userTasks = allUserTasks.filter((task) =>
          !(task.status === 'COMPLETE' && moment(task.dueDate).isBefore(moment().startOf('day').utc())),
        );
        const myTasksCount = userTasks.filter((task) => task.status !== 'COMPLETE').length;

        setTodaysTasks(userTasks);
        setDueTodayCount(myTasksCount);
        taskRefreshSetMyTasksCount(myTasksCount);
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, taskRefreshSetMyTasksCount, user?.userRn]);

  const hydrateTaskConnections = React.useCallback(async (taskData) => {
    const connectionRns = taskData?.connections || [];

    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
        try {
          const clientResponse = await backend.client.getClient(connectionRn, false);
          const clientDetail = clientResponse?.clientDetail;
          const [firstName, ...lastNameParts] = clientDetail?.clientName.split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
        try {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn, false);
          const caregiverDetail = caregiverResponse?.caregiverDetail;
          const caregiverName = caregiverDetail?.caregiverName || '';
          const [firstName = '', ...lastNameParts] = caregiverName.trim().split(/\s+/);

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      return null;
    }));

    return {
      ...taskData,
      connectionInfo: connectionInfo.filter(Boolean),
    };
  }, []);

  const handleSetStatusToComplete = React.useCallback((taskRn, isComplete) => {
    if (isComplete) return;

    const currentTask = todaysTasks.find((task) => task.taskRn === taskRn);

    if (!currentTask) {
      return;
    }

    const completedTask = {
      ...currentTask,
      status: 'COMPLETE',
    };

    setTodaysTasks((previousTasks) =>
      previousTasks.map((task) => task.taskRn === taskRn ? completedTask : task),
    );
    setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
    taskRefreshDecrementMyTasksCount();
    setIsLoading(true);

    progressWrap(async () => {
      try {
        await backend.task.updateTaskStatus(taskRn, 'COMPLETE');
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, taskRefreshDecrementMyTasksCount, todaysTasks]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const openDialogs = {
    addTask: () => {
      setEditingTaskRn(null);
      setIsEditorOpen(true);
    },
    editTask: (task) => {
      setIsEditorOpen(false);
      setEditingTaskRn(task.taskRn);
    },
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingTaskRn(null);
  };
  const edits = {
    addTask: (taskData) => {
      progressWrap(async () => {
        try {
          const createdTaskResponse = await backend.task.addTask(taskData);
          const createdTask = createdTaskResponse.task;
          const ownerRns = createdTask.ownerRns || [];
          const isAssignedToCurrentUser = ownerRns.includes(user.userRn);
          const isDueToday = moment(createdTask?.dueDate).isBetween(
              moment().startOf('day'),
              moment().endOf('day'),
              null,
              '[]',
          );

          if (isAssignedToCurrentUser && isDueToday) {
            const taskWithInfo = await hydrateTaskConnections(createdTask);

            setTodaysTasks((previousTasks) =>
              [...previousTasks, taskWithInfo].sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf()),
            );
            if (createdTask.status !== 'COMPLETE') {
              setDueTodayCount((previousCount) => previousCount + 1);
              taskRefreshIncrementMyTasksCount();
            }
          }
        } finally {
          closeEditor();
        }
      });
    },
    editTask: (taskRn, taskData) => {
      const currentTask = todaysTasks.find((task) => task.taskRn === taskRn);

      progressWrap(async () => {
        try {
          const updatedTaskResponse = await backend.task.updateTask(taskRn, taskData);
          const updatedTask = updatedTaskResponse.task;
          const ownerRns = updatedTask.ownerRns || [];
          const isAssignedToCurrentUser = ownerRns.includes(user.userRn);

          if (isAssignedToCurrentUser) {
            const taskWithInfo = await hydrateTaskConnections(updatedTask);
            setTodaysTasks((previousTasks) => {
              const filteredTasks = previousTasks.filter((task) => task.taskRn !== taskRn);
              return [...filteredTasks, taskWithInfo].sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());
            });
          } else {
            setTodaysTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskRn));
          }

          if (currentTask.status !== 'COMPLETE' && (!isAssignedToCurrentUser || updatedTask.status === 'COMPLETE')) {
            setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
            taskRefreshDecrementMyTasksCount();
          }
          if (currentTask.status === 'COMPLETE' && isAssignedToCurrentUser && updatedTask.status !== 'COMPLETE') {
            setDueTodayCount((previousCount) => previousCount + 1);
            taskRefreshIncrementMyTasksCount();
          }
        } finally {
          closeEditor();
        }
      });
    },
    removeTask: (taskData) => {
      setTodaysTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));
      if (taskData.status !== 'COMPLETE') {
        setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
        taskRefreshDecrementMyTasksCount();
      }

      progressWrap(async () => {
        try {
          await backend.task.removeTask(taskData.taskRn);
        } finally {
          setEditingTaskRn(null);
        }
      });
    },
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        pt: 4,
        pb: 20,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {translate('tasks.MyDay.Header.Greeting', {userFirstName: user.firstName})}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.MyDay.Header.Summary', {todayDate: moment().format('dddd MMM DD'), numTasks: dueTodayCount})}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={1.5}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontSize: '16px',
            fontWeight: 700,
            color: 'grey.500',
          }}
        >
          {translate('tasks.MyDay.Sections.NeedsYou')}
        </Typography>
        <Stack
          spacing={1.5}
        >
          {
            isLoading && (
              <TaskListSkeleton />
            )
          }
          {
            !isLoading && todaysTasks.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {translate('tasks.MyDay.Empty')}
              </Typography>
            )
          }
          {
            !isLoading && todaysTasks.map((task) => (
              editingTaskRn === task.taskRn ?
                <TaskEditor
                  key={task.taskRn}
                  isEditing
                  task={task}
                  onCancel={closeEditor}
                  onSubmit={(payload) => edits.editTask(task.taskRn, payload)}
                /> :
                <TaskItem
                  key={task.taskRn}
                  task={task}
                  handleSetStatusToComplete={handleSetStatusToComplete}
                  onClickEditTask={openDialogs.editTask}
                  removeTask={edits.removeTask}
                />
            ))
          }
          {
            isEditorOpen && !editingTaskRn && (
              <TaskEditor
                task={{
                  ownerInfo: [{
                    ownerRn: user.userRn,
                    firstName: user.firstName,
                    lastName: user.lastName,
                  }],
                }}
                onCancel={closeEditor}
                onSubmit={edits.addTask}
              />
            )
          }
          {
            !isEditorOpen && !editingTaskRn && (
              <Box
                component='button'
                onClick={openDialogs.addTask}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: 'primary.main',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  p: 0,
                  mt: 1,
                }}
              >
                <Box component='span'>+</Box>
                <Translate expression='tasks.Common.NewTask' />
              </Box>
            )
          }
        </Stack>
      </Stack>
    </Stack>
  );
};

MyDay.propTypes = {
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  taskRefreshSetMyTasksCount: PropTypes.func.isRequired,
  taskRefreshIncrementMyTasksCount: PropTypes.func.isRequired,
  taskRefreshDecrementMyTasksCount: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(MyDay));
